import { Editor } from '@tiptap/core';
import { PluginKey, TextSelection } from '@tiptap/pm/state';
import { exitSuggestion } from '@tiptap/suggestion';

import './slashGlobals';

export const slashCommandsPluginKey = new PluginKey('slashCommands');
let suppressAutoReopenUntilTs = 0;
let lastSlashMenuOpenedAtTs = 0;
type SyntheticSlashMeta = { createdAt: number; from: number; to: number };
const syntheticSlashTriggerByEditor = new WeakMap<Editor, SyntheticSlashMeta>();
const syntheticSlashTriggerByHost = new Map<string, SyntheticSlashMeta>();
const dismissedSlashTriggerByEditor = new WeakMap<Editor, number>();

export const isSlashDebugEnabled = () => window.__debugSlash === true;
export const isSlashReplayEnabled = () => window.__slashReplayEnabled !== false;
export const isSlashRetryEnabled = () => window.__slashRetryEnabled !== false;
export const isSlashTriggerBoundary = (textBeforeSlash: string) =>
  textBeforeSlash.length === 0 || /\s$/.test(textBeforeSlash);

const getDismissedSlashTrigger = (editor: Editor) => {
  const slashFrom = dismissedSlashTriggerByEditor.get(editor);
  if (slashFrom === undefined) return null;
  if (editor.state.doc.textBetween(slashFrom, slashFrom + 1, '\n', '\n') === '/') return slashFrom;
  dismissedSlashTriggerByEditor.delete(editor);
  return null;
};

export const dismissSlashTrigger = (editor: Editor, slashFrom: number) => {
  dismissedSlashTriggerByEditor.set(editor, slashFrom);
};

export const isSlashTriggerDismissed = (editor: Editor, slashFrom: number) =>
  getDismissedSlashTrigger(editor) === slashFrom;

export const getEditorViewSafe = (editor: Editor) => {
  try {
    return editor.view;
  } catch {
    return null;
  }
};

export const isEditorViewReady = (editor: Editor) => {
  try {
    const view = editor.view;
    const dom = view?.dom as HTMLElement | null | undefined;
    return Boolean(view && dom?.isConnected);
  } catch {
    return false;
  }
};

export const hasPendingSlashTrigger = (editor: Editor) => {
  getDismissedSlashTrigger(editor);
  const { selection } = editor.state;
  if (!selection.empty) return false;
  const { $from } = selection;
  if (!$from.parent.isTextblock || $from.parent.type.name === 'codeBlock') return false;
  const textBefore = editor.state.doc.textBetween($from.start(), selection.from, '\n', '\n');
  const slashMatch = /\/\S*$/.exec(textBefore);
  if (!slashMatch || !isSlashTriggerBoundary(textBefore.slice(0, slashMatch.index))) return false;
  return !isSlashTriggerDismissed(editor, $from.start() + slashMatch.index);
};

export const isSlashMenuVisible = () =>
  Boolean(
    document.querySelector('.slash-menu') ||
      document.querySelector('[data-theme~="slash-menu"]') ||
      document.querySelector('.slash-menu-wrapper'),
  );

export const markSlashMenuOpened = () => {
  lastSlashMenuOpenedAtTs = Date.now();
};

const resolveEditorHostKey = (editor: Editor) => {
  const editorElementOption = editor.options.element;
  const editorHostElement =
    editorElementOption instanceof HTMLElement
      ? editorElementOption
      : editorElementOption &&
          typeof editorElementOption === 'object' &&
          'mount' in editorElementOption &&
          editorElementOption.mount instanceof HTMLElement
        ? editorElementOption.mount
        : null;
  return editorHostElement?.closest('[id*="super-text-root"]')?.id ?? null;
};

const removeFloatingSlashSearchPills = () => {
  document.querySelectorAll('.slash-menu__search-floating').forEach(node => node.remove());
};

const removeStaleSlashTippyRoots = () => {
  document.querySelectorAll('.tippy-box[data-theme~="slash-menu"]').forEach(box => {
    const root = box.closest('[data-tippy-root]');
    if (root instanceof HTMLElement && root.isConnected) {
      root.remove();
      return;
    }
    if (box instanceof HTMLElement && box.isConnected) {
      box.remove();
    }
  });
};

const removeOrphanSlashWrappers = () => {
  document.querySelectorAll('.slash-menu-wrapper').forEach(wrapper => {
    if (!wrapper.closest('[data-tippy-root]')) {
      wrapper.remove();
    }
  });
};

export const forceRemoveStaleSlashMenuUi = () => {
  removeFloatingSlashSearchPills();
  removeStaleSlashTippyRoots();
  removeOrphanSlashWrappers();
};

export const markSyntheticSlashTrigger = (editor: Editor, range?: { from: number; to: number }) => {
  const { from, to } = range ?? { from: -1, to: -1 };
  const meta = { createdAt: Date.now(), from, to };
  syntheticSlashTriggerByEditor.set(editor, meta);
  const hostKey = resolveEditorHostKey(editor);
  if (hostKey) {
    syntheticSlashTriggerByHost.set(hostKey, meta);
  }
};

export const hasSyntheticSlashTrigger = (editor: Editor, maxAgeMs = 5000) => {
  const meta =
    syntheticSlashTriggerByEditor.get(editor) ??
    (() => {
      const hostKey = resolveEditorHostKey(editor);
      return hostKey ? (syntheticSlashTriggerByHost.get(hostKey) ?? null) : null;
    })();
  if (!meta) return false;
  const isFresh = Date.now() - meta.createdAt <= maxAgeMs;
  if (!isFresh) {
    const hostKey = resolveEditorHostKey(editor);
    if (hostKey) {
      syntheticSlashTriggerByHost.delete(hostKey);
    }
    syntheticSlashTriggerByEditor.delete(editor);
  }
  return isFresh;
};

export const clearSyntheticSlashTrigger = (editor: Editor) => {
  const hostKey = resolveEditorHostKey(editor);
  if (hostKey) {
    syntheticSlashTriggerByHost.delete(hostKey);
  }
  syntheticSlashTriggerByEditor.delete(editor);
};

export const getSyntheticSlashTriggerRange = (editor: Editor) => {
  const meta =
    syntheticSlashTriggerByEditor.get(editor) ??
    (() => {
      const hostKey = resolveEditorHostKey(editor);
      return hostKey ? (syntheticSlashTriggerByHost.get(hostKey) ?? null) : null;
    })();
  if (!meta || meta.from < 1 || meta.to <= meta.from) return null;
  return { from: meta.from, to: meta.to };
};

const markSlashAutoReopenSuppressed = () => {
  suppressAutoReopenUntilTs = Date.now();
};

export const isSlashAutoReopenSuppressed = (windowMs = 400) =>
  suppressAutoReopenUntilTs > 0 ? Date.now() - suppressAutoReopenUntilTs < windowMs : false;

export const clearSlashAutoReopenSuppression = () => {
  suppressAutoReopenUntilTs = 0;
};

export const closeSlashSuggestion = (editor: Editor, suppressAutoReopen = true) => {
  const closeRequestedAt = Date.now();
  if (suppressAutoReopen) {
    markSlashAutoReopenSuppressed();
  }
  const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
  const view = getEditorViewSafe(editor);
  if (view) {
    exitSuggestion(view, slashCommandsPluginKey);
  }
  if (!slashState?.active) {
    forceRemoveStaleSlashMenuUi();
  }

  const cleanupIfInactive = () => {
    if (lastSlashMenuOpenedAtTs > closeRequestedAt) return;
    const nextSlashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
    const menuVisible = isSlashMenuVisible();
    if (!nextSlashState?.active || (suppressAutoReopen && !menuVisible)) {
      forceRemoveStaleSlashMenuUi();
    }
  };
  window.setTimeout(cleanupIfInactive, 0);
  window.setTimeout(cleanupIfInactive, 120);
  window.setTimeout(cleanupIfInactive, 320);
};

export const replaySlashTokenAtCursor = (editor: Editor) => {
  const view = getEditorViewSafe(editor);
  if (!view) return false;
  const dom = view.dom as HTMLElement | null | undefined;
  if (dom && document.activeElement !== dom) {
    dom.focus();
  }
  if (!view.hasFocus()) {
    view.focus();
  }

  const { state } = view;
  const { from, $from } = state.selection;
  const textBefore = state.doc.textBetween($from.start(), from, '\n', '\n');
  const slashMatch = /\/\S*$/.exec(textBefore);
  if (!slashMatch || !isSlashTriggerBoundary(textBefore.slice(0, slashMatch.index))) return false;

  const slashIndex = slashMatch.index;
  const slashFrom = $from.start() + slashIndex;
  if (isSlashTriggerDismissed(editor, slashFrom)) return false;
  const slashTo = slashFrom + 1;
  const slashChar = state.doc.textBetween(slashFrom, slashTo, '\n', '\n');
  if (slashChar !== '/') return false;

  const tr = state.tr.delete(slashFrom, slashTo).insertText('/', slashFrom, slashFrom);
  const nextSelection = Math.min(from, tr.doc.content.size);
  tr.setSelection(TextSelection.create(tr.doc, Math.max(1, nextSelection)));
  view.dispatch(tr);
  view.focus();
  return true;
};
